import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';

import { ButtonUI, InputUI, PhoneBase } from '../Interface';
import { NotificationType } from '../../config';
import newDocService from '../../service/newDoc.service';
import addNotification from '../../utils/addNotification';

const defaultDocData = null;

const QuickCustomStatusMobile = () => {
  const inputRef = useRef(null);
  const [barcodeValue, setBarcodeValue] = useState('');
  const [docData, setDocData] = useState(defaultDocData);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const focusInput = () => {
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const resetForm = () => {
    setBarcodeValue('');
    setDocData(defaultDocData);
    focusInput();
  };

  const formatDate = (value) => {
    if (!value) return '-';

    const formatted = dayjs(value);
    return formatted.isValid() ? formatted.format('YYYY.MM.DD') : '-';
  };

  const handleFetchDocument = () => {
    const trimmedValue = barcodeValue.trim();

    if (!trimmedValue || loading) {
      return;
    }

    setDocData(defaultDocData);
    setLoading(true);

    newDocService
      .getQuickCustomStatusMobile(trimmedValue)
      .then((data) => {
        setDocData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleUpdateDocument = () => {
    if (!docData?.docId || !docData?.docTypeId || loading) {
      return;
    }

    setLoading(true);

    newDocService
      .updateQuickCustomStatusMobile({
        docId: docData.docId,
        docTypeId: docData.docTypeId,
      })
      .then(() => {
        addNotification({
          content: 'A dokumentum frissítése sikeres volt.',
          type: NotificationType.SUCCESS,
        });
        resetForm();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <PhoneBase name="QuickCustomStatusMobile" sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 0 }}>
          Gyors státusz frissítés
        </Typography>
        <IconButton color="error" aria-label="Törlés" onClick={resetForm} disabled={!barcodeValue && !docData}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          minHeight: 80,
          mt: 1.5,
          p: 1.5,
          bgcolor: 'grey.100',
        }}
      >
        <Typography variant="h7" component="h4">
          Vonalkód
        </Typography>

        <Box sx={{ mt: 1, bgcolor: 'white' }}>
          <InputUI
            autoFocus
            fullWidth
            size="small"
            placeholder="Bizonylatszám / vonalkód"
            value={barcodeValue}
            onChange={(e) => setBarcodeValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleFetchDocument();
              }
            }}
            inputRef={inputRef}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton size="small" edge="end" aria-label="Keresés" onClick={handleFetchDocument}>
                    <SearchIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {docData && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            minHeight: 80,
            mt: 1.5,
            p: 1.5,
            bgcolor: 'grey.100',
          }}
        >
          <Typography variant="h7" component="h4">
            Bizonylat adatok
          </Typography>

          <Stack spacing={1} sx={{ mt: 1 }}>
            <Box sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
                {docData.docNum}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {docData.companyCode} - {docData.companyName}
              </Typography>
            </Box>

            <Box sx={{ px: 1.5, py: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'white' }}>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Bizonylat státusz:
                </Box>{' '}
                {docData.docStatus || '-'}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Egyedi státusz:
                </Box>{' '}
                {docData.customStatus || '-'}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Dátum:
                </Box>{' '}
                {formatDate(docData.docDate)}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Kiállítás:
                </Box>{' '}
                {formatDate(docData.issueDate)}
              </Typography>
              <Typography variant="body2">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  Határidő:
                </Box>{' '}
                {formatDate(docData.dueDate)}
              </Typography>
            </Box>
          </Stack>
        </Box>
      )}

      {docData && (
        <Box textAlign="center" mt="2rem">
          <ButtonUI text="Rögzítés" className="bg-success" onClick={handleUpdateDocument} disabled={loading} />
        </Box>
      )}
    </PhoneBase>
  );
};

export default QuickCustomStatusMobile;
